import { Injectable } from '@angular/core';
import {
  forkJoin,
  Observable,
  of,
} from 'rxjs';
import {
  catchError,
  map,
  shareReplay,
} from 'rxjs/operators';

import { ItemDataService } from '../../data/item-data.service';
import { RemoteData } from '../../data/remote-data';
import { getFirstCompletedRemoteData } from '../../shared/operators';
import { Item } from '../../shared/item.model';
import { DSONameService } from '../../breadcrumbs/dso-name.service';
import {
  hasValue,
  isNotEmpty,
} from '../../../shared/empty.util';

/**
 * Service to resolve UUIDs to human-readable display names
 * Used primarily for search filter facets that return UUIDs instead of names
 */
@Injectable({
  providedIn: 'root',
})
export class UuidLabelResolverService {

  /**
   * Regex pattern to match UUIDs (format: 8-4-4-4-12 hexadecimal characters)
   */
  private readonly uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

  /**
   * Cache to store resolved UUIDs to avoid redundant API calls
   * Key: UUID, Value: Observable of display name
   */
  private readonly cache = new Map<string, Observable<string>>();

  constructor(
    protected itemDataService: ItemDataService,
    protected dsoNameService: DSONameService,
  ) {
  }

  /**
   * Checks if a string is a valid UUID format
   * @param value The string to check
   * @returns true if the string matches UUID format
   */
  isUuid(value: string): boolean {
    if (!isNotEmpty(value)) {
      return false;
    }
    return this.uuidRegex.test(value.trim());
  }

  /**
   * Resolves a UUID to its display name
   * Uses caching to avoid redundant API calls for the same UUID
   *
   * @param uuid The UUID to resolve
   * @param filterName Optional filter name for context (e.g., 'relation.isAuthorOfPublication.latestForDiscovery')
   * @returns Observable of the display name, or the original UUID if resolution fails
   */
  resolveUuidToLabel(uuid: string, filterName?: string): Observable<string> {
    if (!this.isUuid(uuid)) {
      return of(uuid);
    }

    const trimmedUuid = uuid.trim();

    // Check cache first
    if (this.cache.has(trimmedUuid)) {
      return this.cache.get(trimmedUuid);
    }

    // Create new resolution observable
    const resolution$ = this.itemDataService.findById(trimmedUuid, true, false).pipe(
      getFirstCompletedRemoteData(),
      map((itemRD: RemoteData<Item>) => {
        if (hasValue(itemRD) && hasValue(itemRD.payload)) {
          // Use DSONameService to get the proper display name
          // This handles Person (familyName, givenName) and OrgUnit (legalName) automatically
          const displayName = this.dsoNameService.getName(itemRD.payload);
          return isNotEmpty(displayName) ? displayName : trimmedUuid;
        }
        return trimmedUuid;
      }),
      catchError((error) => {
        // Log error for debugging (optional)
        console.warn(`Failed to resolve UUID ${trimmedUuid} for filter ${filterName}:`, error);
        return of(trimmedUuid);
      }),
      shareReplay({ bufferSize: 1, refCount: false }), // Cache the result and keep subscription alive
    );

    // Store in cache
    this.cache.set(trimmedUuid, resolution$);

    return resolution$;
  }

  /**
   * Resolves a value that might be a UUID
   * If it's not a UUID, returns it as-is
   *
   * @param value The value to potentially resolve
   * @param filterName Optional filter name for context
   * @returns Observable of the resolved display name or original value
   */
  resolveValue(value: string, filterName?: string): Observable<string> {
    if (!isNotEmpty(value)) {
      return of(value);
    }

    if (this.isUuid(value)) {
      return this.resolveUuidToLabel(value, filterName);
    }

    return of(value);
  }

  /**
   * Batch resolves multiple UUIDs at once
   * Useful for pre-resolving all facet values when they're first loaded
   *
   * @param values Array of values that might be UUIDs
   * @param filterName Optional filter name for context
   * @returns Observable that emits when all resolutions are complete (for pre-loading)
   */
  batchResolveValues(values: string[], filterName?: string): Observable<string[]> {
    if (!values || values.length === 0) {
      return of([]);
    }

    // Resolve all values in parallel
    const resolutionObservables = values.map(value => this.resolveValue(value, filterName));
    
    return forkJoin(resolutionObservables).pipe(
      catchError((error) => {
        console.warn(`Batch resolution failed for filter ${filterName}:`, error);
        // Return original values on error
        return of(values);
      }),
    );
  }

  /**
   * Strips operator from a filter value (e.g., "uuid,equals" -> "uuid")
   * @param value The value that might contain an operator
   * @returns The value without the operator
   */
  private stripOperator(value: string): string {
    if (!value) {
      return value;
    }
    // Check if value ends with ,equals, ,query, or ,authority
    const operatorMatch = value.match(/^(.+),(equals|query|authority|contains|notcontains|notequals)$/);
    if (operatorMatch) {
      return operatorMatch[1];
    }
    return value;
  }

  /**
   * Pre-resolves UUIDs from facet values
   * This triggers the API calls immediately so labels are ready when displayed
   *
   * @param facetValues Array of facet values to pre-resolve
   * @param filterName Optional filter name for context
   */
  preResolveFacetValues(facetValues: any[], filterName?: string): void {
    if (!facetValues || facetValues.length === 0) {
      return;
    }

    // Extract all values that might be UUIDs
    const valuesToResolve: string[] = [];
    
    facetValues.forEach(fv => {
      // Try different properties where the value might be stored
      let rawValue: string = null;
      
      if (fv.value) {
        rawValue = fv.value;
      } else if (fv.label) {
        rawValue = fv.label;
      } else if (fv.authorityKey) {
        rawValue = fv.authorityKey;
      } else if (typeof fv === 'string') {
        rawValue = fv;
      }
      
      if (rawValue) {
        // Strip operator if present (e.g., "uuid,equals" -> "uuid")
        const cleanValue = this.stripOperator(rawValue);
        
        // Check if it's a UUID after stripping the operator
        if (this.isUuid(cleanValue)) {
          valuesToResolve.push(cleanValue);
        }
      }
    });

    // Trigger resolution for all UUIDs by subscribing to each one
    // This ensures the HTTP requests are fired immediately
    if (valuesToResolve.length > 0) {
      // Use Set to avoid duplicate UUIDs
      const uniqueUuids = Array.from(new Set(valuesToResolve));
      
      uniqueUuids.forEach(uuid => {
        // Subscribe to trigger the API call and populate cache
        // The subscription ensures the HTTP request is fired immediately
        this.resolveUuidToLabel(uuid, filterName).subscribe({
          next: () => {
            // Resolution triggered, cache is populated
          },
          error: (error) => {
            console.warn(`Pre-resolution failed for UUID ${uuid} in filter ${filterName}:`, error);
          },
        });
      });
    }
  }

  /**
   * Clears the resolution cache
   * Useful for testing or when you want to force fresh lookups
   */
  clearCache(): void {
    this.cache.clear();
  }
}

